import { useEffect, useState } from "react";
import axios from "axios";
import { MessageSquarePlus, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialForm = { name: "", rating: 5, message: "" };

export const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const loadReviews = async () => {
    const response = await axios.get(`${API}/reviews`);
    setReviews(response.data || []);
  };

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        await loadReviews();
      } catch (error) {
        setReviews([]);
      }
    };
    fetchReviews();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please add your name and review.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Review submitted. Thank you!");
      setForm(initialForm);
      await loadReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not submit review.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reviews" className="px-6 py-16 lg:px-8 lg:py-20" data-testid="reviews-section">
      <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start" data-testid="reviews-grid">
        <div data-testid="reviews-heading-block">
          <p className="font-display text-xs uppercase tracking-[0.24em] text-blue-300" data-testid="reviews-eyebrow">Customer reviews</p>
          <h2 className="mt-4 font-display text-4xl font-light tracking-[-0.04em] text-white sm:text-5xl" data-testid="reviews-title">Share your real experience.</h2>
          <p className="mt-5 leading-7 text-zinc-400" data-testid="reviews-description">Leave a review after your service. Submitted reviews appear here.</p>
          <form onSubmit={handleSubmit} className="mt-8 rounded-[2rem] border border-white/10 bg-white/[0.04] p-5" data-testid="review-submit-form">
            <Input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Your name" className="h-12 rounded-2xl border-white/10 bg-white/[0.05] text-white placeholder:text-zinc-500" data-testid="review-name-input" />
            <div className="mt-4 flex items-center gap-2" data-testid="review-rating-control">
              {Array.from({ length: 5 }).map((_, index) => {
                const rating = index + 1;
                return (
                  <button key={rating} type="button" onClick={() => setForm((current) => ({ ...current, rating }))} className="rounded-full p-1 text-amber-200 transition-transform hover:-translate-y-0.5" data-testid={`review-rating-button-${rating}`} aria-label={`${rating} star rating`}>
                    <Star className={`h-6 w-6 ${form.rating >= rating ? "fill-current" : ""}`} />
                  </button>
                );
              })}
            </div>
            <Textarea value={form.message} onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))} placeholder="How did the service go?" className="mt-4 min-h-28 rounded-2xl border-white/10 bg-white/[0.05] text-white placeholder:text-zinc-500" data-testid="review-message-textarea" />
            <Button disabled={loading} className="mt-5 h-12 w-full rounded-full bg-blue-500 text-white hover:bg-blue-400" data-testid="review-submit-button">
              <MessageSquarePlus className="h-4 w-4" /> {loading ? "Submitting..." : "Submit review"}
            </Button>
          </form>
        </div>

        <div className="grid gap-4" data-testid="reviews-list">
          {reviews.map((review) => (
            <article key={review.id} className="rounded-[1.5rem] border border-white/10 bg-black/25 p-5" data-testid={`public-review-card-${review.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3" data-testid={`public-review-header-${review.id}`}>
                <p className="font-display text-xl text-white" data-testid={`public-review-name-${review.id}`}>{review.name}</p>
                <span className="flex text-amber-200" data-testid={`public-review-rating-${review.id}`}>{Array.from({ length: Number(review.rating || 0) }).map((_, index) => <Star key={index} className="h-4 w-4 fill-current" />)}</span>
              </div>
              <p className="mt-3 text-sm leading-6 text-zinc-300" data-testid={`public-review-message-${review.id}`}>{review.message}</p>
            </article>
          ))}
          {reviews.length === 0 && (
            <div className="rounded-[1.5rem] border border-dashed border-white/15 bg-white/[0.03] p-6 text-zinc-400" data-testid="reviews-empty-state">No reviews submitted yet.</div>
          )}
        </div>
      </div>
    </section>
  );
};